import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Calendar, Clock, Send, Tag, Wrench } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { getUtilityTemplates, UtilityTemplate } from '@/services/api/broadcast';

const MESSAGE_TAG_OPTIONS: { value: string; label: string }[] = [
    { value: 'ACCOUNT_UPDATE', label: 'ACCOUNT_UPDATE - Mudanças de conta' },
    { value: 'CONFIRMED_EVENT_UPDATE', label: 'CONFIRMED_EVENT_UPDATE - Eventos confirmados' },
    { value: 'POST_PURCHASE_UPDATE', label: 'POST_PURCHASE_UPDATE - Pós-compra' },
    { value: 'CUSTOMER_FEEDBACK', label: 'CUSTOMER_FEEDBACK - Pesquisas de satisfação' },
    { value: 'HUMAN_AGENT', label: 'HUMAN_AGENT - Atendimento humano' },
];

import type { BroadcastType } from '../types/broadcast.types';

interface StepScheduleProps {
    isScheduled: boolean;
    scheduledTime: string;
    broadcastType?: BroadcastType;
    messageTag?: string | null;
    utilityTemplateId?: number | null;
    pageId?: string;
    horaLocalApenas?: boolean;
    horaLocal?: string;
    onHoraLocalChange?: (value: string) => void;
    onIsScheduledChange: (value: boolean) => void;
    onScheduledTimeChange: (value: string) => void;
    onBroadcastTypeChange?: (type: BroadcastType, tag: string | null) => void;
    onUtilityTemplateChange?: (id: number | null) => void;
}

export default function StepSchedule({
                                         isScheduled,
                                         scheduledTime,
                                         broadcastType = 'standard',
                                         messageTag = null,
                                         utilityTemplateId = null,
                                         pageId,
                                         horaLocalApenas = false,
                                         horaLocal = '',
                                         onHoraLocalChange,
                                         onIsScheduledChange,
                                         onScheduledTimeChange,
                                         onBroadcastTypeChange,
                                         onUtilityTemplateChange,
                                     }: StepScheduleProps) {
    const minDateTime = new Date();
    minDateTime.setMinutes(minDateTime.getMinutes() + 5);
    const minDateTimeString = minDateTime.toISOString().slice(0, 16);

    const [utilityTemplates, setUtilityTemplates] = useState<UtilityTemplate[]>([]);

    useEffect(() => {
        if (broadcastType !== 'utility' || !pageId) return;
        getUtilityTemplates(pageId)
            .then(templates => setUtilityTemplates(templates.filter(t => t.status === 'approved')))
            .catch(() => setUtilityTemplates([]));
    }, [broadcastType, pageId]);

    if (horaLocalApenas) {
        return (
            <div className="space-y-6">
                <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900">Agendamento</h3>
                    <p className="text-sm text-gray-600 mt-2">
                        Escolha a hora em que a rotina dispara
                    </p>
                </div>
                <div className="max-w-md mx-auto space-y-2">
                    <Label htmlFor="hora_local" className="text-sm font-medium flex items-center gap-2">
                        <Clock className="h-4 w-4 text-[#7dd3fc]" />
                        Hora local *
                    </Label>
                    <Input
                        id="hora_local"
                        type="time"
                        value={horaLocal}
                        onChange={(e) => onHoraLocalChange?.(e.target.value)}
                        className="text-base"
                    />
                    <p className="text-xs text-gray-500">
                        Hora local de cada página. 07:15 sai às 04:15, 05:15 e 06:15 UTC para páginas em UTC+3, +2 e +1 — todas às 07:15 para quem recebe.
                    </p>
                </div>
                {/* Standard e tag são a mesma etapa 1 ("Normal"), então a escolha da
                    tag vive aqui — inclusive ao EDITAR um grupo, que entra direto neste
                    passo e antes não tinha como trocar a tag. Utility e Esteira têm
                    etapa própria e não aparecem. */}
                {onBroadcastTypeChange
                    && (broadcastType === 'standard' || broadcastType === 'message_tag') && (
                    <div className="max-w-md mx-auto space-y-3">
                        <Label className="text-sm font-medium text-gray-700">JANELA DE ENVIO</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => onBroadcastTypeChange('standard', null)}
                                className={`p-4 border-2 rounded-xl text-left transition-all ${broadcastType === 'standard'
                                    ? 'border-[#7dd3fc] bg-[#7dd3fc]/5'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${broadcastType === 'standard' ? 'border-[#7dd3fc]' : 'border-gray-300'}`}>
                                        {broadcastType === 'standard' && <div className="w-2 h-2 rounded-full bg-[#7dd3fc]" />}
                                    </div>
                                    <span className="font-semibold text-gray-900">Sem tag</span>
                                </div>
                                <p className="text-xs text-gray-500">Só quem interagiu nas últimas 24h recebe.</p>
                            </button>
                            <button
                                type="button"
                                onClick={() => onBroadcastTypeChange('message_tag', messageTag || 'ACCOUNT_UPDATE')}
                                className={`p-4 border-2 rounded-xl text-left transition-all ${broadcastType === 'message_tag'
                                    ? 'border-[#7dd3fc] bg-[#7dd3fc]/5'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Tag className={`w-4 h-4 ${broadcastType === 'message_tag' ? 'text-[#7dd3fc]' : 'text-gray-400'}`} />
                                    <span className="font-semibold text-gray-900">Com message tag</span>
                                </div>
                                <p className="text-xs text-gray-500">Sai fora da janela de 24h.</p>
                            </button>
                        </div>
                        {broadcastType === 'message_tag' && (
                            <div className="pt-1">
                                <Label className="text-xs text-gray-500 block mb-1.5">Categoria da Message Tag</Label>
                                <Select value={messageTag || 'ACCOUNT_UPDATE'} onValueChange={(v) => onBroadcastTypeChange('message_tag', v)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Selecione uma categoria..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MESSAGE_TAG_OPTIONS.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="mt-1.5 text-xs text-gray-500">
                                    A tag vale para todo disparo que esta Rotina criar, todo dia.
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Agendamento</h3>
                <p className="text-sm text-gray-600 mt-2">
                    Escolha quando deseja enviar seu broadcast
                </p>
            </div>

            <div className="max-w-md mx-auto space-y-6">
                {/* TIPO DE BROADCAST */}
                {onBroadcastTypeChange && (
                    <div className="space-y-3">
                        <Label className="text-sm font-medium text-gray-700">TIPO DE BROADCAST</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <button
                                type="button"
                                onClick={() => onBroadcastTypeChange('standard', null)}
                                className={`p-4 border-2 rounded-xl text-left transition-all ${broadcastType === 'standard'
                                    ? 'border-[#7dd3fc] bg-[#7dd3fc]/5'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${broadcastType === 'standard' ? 'border-[#7dd3fc]' : 'border-gray-300'}`}>
                                        {broadcastType === 'standard' && <div className="w-2 h-2 rounded-full bg-[#7dd3fc]" />}
                                    </div>
                                    <span className="font-semibold text-gray-900">Standard</span>
                                </div>
                                <p className="text-xs text-gray-500">Janela de 24h padrão.</p>
                            </button>
                            <button
                                type="button"
                                onClick={() => onBroadcastTypeChange('message_tag', messageTag || 'ACCOUNT_UPDATE')}
                                className={`p-4 border-2 rounded-xl text-left transition-all ${broadcastType === 'message_tag'
                                    ? 'border-[#7dd3fc] bg-[#7dd3fc]/5'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Tag className={`w-4 h-4 ${broadcastType === 'message_tag' ? 'text-[#7dd3fc]' : 'text-gray-400'}`} />
                                    <span className="font-semibold text-gray-900">Message Tag</span>
                                </div>
                                <p className="text-xs text-gray-500">Envio fora da janela.</p>
                            </button>
                            <button
                                type="button"
                                onClick={() => onBroadcastTypeChange('utility', 'CONFIRMED_EVENT_UPDATE')}
                                className={`p-4 border-2 rounded-xl text-left transition-all ${broadcastType === 'utility'
                                    ? 'border-[#7dd3fc] bg-[#7dd3fc]/5'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Wrench className={`w-4 h-4 ${broadcastType === 'utility' ? 'text-[#7dd3fc]' : 'text-gray-400'}`} />
                                    <span className="font-semibold text-gray-900">Mensagem de Utilidade</span>
                                </div>
                                <p className="text-xs text-gray-500">Template de utilidade.</p>
                            </button>
                        </div>
                        {broadcastType === 'message_tag' && (
                            <div className="pt-2">
                                <Label className="text-xs text-gray-500 block mb-1.5">Categoria da Message Tag</Label>
                                <Select value={messageTag || 'ACCOUNT_UPDATE'} onValueChange={(v) => onBroadcastTypeChange('message_tag', v)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Selecione uma categoria..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MESSAGE_TAG_OPTIONS.map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {broadcastType === 'utility' && onUtilityTemplateChange && (
                            <div className="pt-2">
                                <Label className="text-xs text-gray-500 block mb-1.5">Template de Utilidade</Label>
                                {utilityTemplates.length === 0 ? (
                                    <p className="text-xs text-gray-400">Nenhum template aprovado para esta página.</p>
                                ) : (
                                    <Select
                                        value={utilityTemplateId ? String(utilityTemplateId) : ''}
                                        onValueChange={(v) => onUtilityTemplateChange(v ? Number(v) : null)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Selecione um template..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {utilityTemplates.map((t) => (
                                                <SelectItem key={t.id} value={String(t.id)}>
                                                    <span className="font-medium">{t.title || t.template_name}</span>
                                                    {t.subtitle && <span className="text-gray-400 ml-2 text-xs">{t.subtitle}</span>}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* HORÁRIO DE DISPARO */}
                <Label className="text-sm font-medium text-gray-700 block">HORÁRIO DE DISPARO</Label>
                {/* Opção de Envio Imediato ou Agendado */}
                <div className="grid grid-cols-2 gap-4">
                    {/* Enviar Agora */}
                    <button
                        onClick={() => onIsScheduledChange(false)}
                        className={`
                            p-6 border-2 rounded-xl transition-all text-center
                            ${!isScheduled
                            ? 'border-[#7dd3fc] bg-[#7dd3fc]/5 shadow-md'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }
                        `}
                    >
                        <Send className={`h-8 w-8 mx-auto mb-3 ${!isScheduled ? 'text-[#7dd3fc]' : 'text-gray-400'}`} />
                        <h4 className="font-semibold text-gray-900">Enviar Agora</h4>
                        <p className="text-xs text-gray-600 mt-2">
                            Envio imediato após confirmar
                        </p>
                    </button>

                    {/* Agendar */}
                    <button
                        onClick={() => onIsScheduledChange(true)}
                        className={`
                            p-6 border-2 rounded-xl transition-all text-center
                            ${isScheduled
                            ? 'border-[#7dd3fc] bg-[#7dd3fc]/5 shadow-md'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }
                        `}
                    >
                        <Calendar className={`h-8 w-8 mx-auto mb-3 ${isScheduled ? 'text-[#7dd3fc]' : 'text-gray-400'}`} />
                        <h4 className="font-semibold text-gray-900">Agendar</h4>
                        <p className="text-xs text-gray-600 mt-2">
                            Escolha data e hora
                        </p>
                    </button>
                </div>

                {/* Seletor de Data e Hora (quando agendado) */}
                {isScheduled && (
                    <div className="space-y-4 pt-4 border-t-2 border-gray-100">
                        <div className="space-y-2">
                            <Label htmlFor="scheduled_time" className="text-sm font-medium flex items-center gap-2">
                                <Clock className="h-4 w-4 text-[#7dd3fc]" />
                                Data e Hora do Envio *
                            </Label>
                            <Input
                                id="scheduled_time"
                                type="datetime-local"
                                min={minDateTimeString}
                                value={scheduledTime}
                                onChange={(e) => onScheduledTimeChange(e.target.value)}
                                className="text-base"
                            />
                            <p className="text-xs text-gray-500">
                                O broadcast será enviado automaticamente na data e hora especificadas,
                                no fuso horário configurado em cada página.
                            </p>
                        </div>

                        {scheduledTime && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-blue-900">
                                            Agendado para:
                                        </p>
                                        <p className="text-sm text-blue-700 mt-1">
                                            {scheduledTime ? new Date(scheduledTime + ':00-03:00').toLocaleString('pt-BR', { dateStyle: 'full', timeStyle: 'short', timeZone: 'America/Sao_Paulo' }) : ''}, no fuso horário de cada página
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Info sobre envio imediato */}
                {!isScheduled && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <Send className="h-5 w-5 text-green-600 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-green-900">
                                    Envio Imediato
                                </p>
                                <p className="text-sm text-green-700 mt-1">
                                    Seu broadcast será enviado assim que você confirmar no próximo passo
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}